<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useProductStore } from '@/stores/productsStore';
import { useKeyboardStore } from '@/stores/keyboardStore';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import AddManagerApprovalRequest from '../Popups/AddManagerApprovalRequest.vue';
import CommentPopup from '../Popups/CommentPopup.vue';
import AddSales from '../Popups/AddSalesPerson.vue';
import ProductPlaceholder from './ProductPlaceholder.vue';

import ToggleSwitch from 'primevue/toggleswitch';

import { useToast } from 'vue-toastification';



const authStore = useAuthStore();
const orderStore = useOrderStore();
const keyboardStore = useKeyboardStore();
const store = useProductStore();
const toast = useToast();


// Props
const props = defineProps({
  order: {
    type: Object,
    required: true
  }
});

// Reactive variables
const isOpen = ref(Array(props.order.orderItems.length).fill(false));
const showCommentPopup = ref(false);
const selectedItemForComment = ref(null);
const backupDiscountPercentage = ref(null);
const selectedItemForSalesmen = ref(null);
// Note: invalidPriceFields is now managed in the OrderStore

function toggleAccordion(index) {
  isOpen.value[index] = !isOpen.value[index];
}

const handleAddSales = (item) => {
  selectedItemForSalesmen.value = item;
  authStore.toggleAddSalesPopup();
};


// Computed property to check if overall discount is applied
const isOverallDiscountApplied = computed(() => props.order.overallDiscount > 0);

// Handle quantity changes and remove item if quantity is zero
function handleInput(item) {
  // if (item.Qty > item.MaxQty) {
  //   item.Qty = item.MaxQty;
  // }
  if (item.Qty === 0) {
    orderStore.deleteOrderItem(item);
  }
}

const getItemStockStatus = (item) => {
  if (item.MaxQty < 1) return 'red';
  if (item.MaxQty < 5) return 'yellow';
  return null;
}

// Store the original values of an item for validation
const originalValues = ref({});

const storeOriginalValue = (key, item) => {
  if (!originalValues.value[item.ItemId]) {
    originalValues.value[item.ItemId] = {};
  }
  if (originalValues.value[item.ItemId][key] === undefined) {
    originalValues.value[item.ItemId][key] = item[key];
  }
};

const userNow = store.users.find(user => user.FullName === authStore.currentUser);
const managerNow = store.users.find(user => user.FullName === authStore.managerUser);

// Handle user allowed to change price up
const userCanEditPrice = store.users.find(user => user.FullName === authStore.currentUser)?.CanIncreasePrice;
const managerCanEditPrice = store.users.find(user => user.FullName === authStore.managerUser)?.CanIncreasePrice;
const userCanAllowFlatTaxRate = store.users.find(user => user.FullName === authStore.managerUser)?.AllowFlatRate;

  // console.log('Allowed discount User Now:', userNow.AllowedDicount);
  // if(discountValue <= userNow.AllowedDicount) {
  //   orderStore.applyOverallDiscount(discountValue);
  //   return;
  // } else {
  //   console.log('Error User:');
  // }

const checkValueChanged = (key, item) => {
  console.log('checkValueChanged triggered for:', key, item);
  
  // Get the current original value from the item
  const currentOriginalValue = parseFloat(item.OriginalPrice || 0);
  const currentValue = parseFloat(item[key] || 0);

  console.log('Current Original Value:', currentOriginalValue);
  console.log('Current Value:', currentValue);

  // Skip manager approval for items with Sku === 'MISC-2' or 'TIP-99 ', BSUR, or offline
  // manual-entry items (no known price — the cashier types it).
  if (item.Sku === 'MISC-2' || item.Sku === 'TIP-99'
  || item.Sku.toLowerCase().startsWith('bsur')
  || item.RetailSku.toLowerCase().startsWith('bsur')
  || item.IsOfflineManualEntry
  ) {
    // currentOriginalValue = currentValue
    // console.log('Sku for Misc', item.Sku);
    return;
  }

  if (key === 'Price') {
    if (currentValue < currentOriginalValue) {
      if ((item.Discount <= userNow?.AllowedDicount) || 
          (authStore.isOnlyManagerLoggedIn && item.Discount <= managerNow?.AllowedDicount)) {
        orderStore.updateDiscountPercentage(item, item.Discount);
      } else {
        console.log('Price has decreased, triggering manager permission check', currentValue, currentOriginalValue);
        checkManagerPermission(item);
      }
    } else if (currentValue > currentOriginalValue) {
      // Update both the item's OriginalPrice and store the new value
      item.OriginalPrice = currentValue;
      originalValues.value[item.ItemId] = originalValues.value[item.ItemId] || {};
      originalValues.value[item.ItemId][key] = currentValue;
      orderStore.saveOrderItemsToLocalStorage();
    }
  } else if (key === 'Discount') {
    // Tip items cannot have discounts
    if (item.Sku === 'TIP-99') {
      item.Discount = 0;
      return;
    }

    if(authStore.isOnlyManagerLoggedIn && currentValue <= managerNow?.AllowedDicount) {
      orderStore.updateDiscountPercentage(item, currentValue);
      return;
    }

    if (currentValue <= userNow?.AllowedDicount) {
      orderStore.updateDiscountPercentage(item, currentValue);
      return;
    } else {
      console.log('Discount exceeds allowed limit, triggering manager permission check', currentValue);
      checkManagerPermission(item);
    }
  }
};
// handle misc and tip item name input
const handleNameInput = (item) => {
  if (item.Sku === 'MISC-2' || item.Sku === 'TIP-99' || item.Sku.startsWith('BSUR')) {
    const existingItem = orderStore.state.orderItems.find(orderItem =>
      orderItem.uniqueId === item.uniqueId
    );

    const nameUsedByAnother = orderStore.state.orderItems.find(orderItem =>
      orderItem.Sku === item.Sku &&
      orderItem.uniqueId !== item.uniqueId &&
      orderItem.ItemName.trim().toLowerCase() === item.ItemName.trim().toLowerCase()
    );

    if (existingItem && !nameUsedByAnother) {
      existingItem.ItemName = item.ItemName;
      existingItem.originalName = item.ItemName;
      orderStore.saveOrderItemsToLocalStorage();
    } else if (existingItem && nameUsedByAnother) {
      toast.warning(`${item.Sku} item name already exists`);
      item.ItemName = existingItem.originalName;
    }
  }
};

// Handle price input and recalculate discount based on the original price
const handlePriceInput = (item) => {
  const itemId = item.uniqueId || item.ItemId;
  
  // Check if price is empty, null, undefined, NaN, or zero
  if (item.Price === null || item.Price === undefined || item.Price === '' || isNaN(item.Price) || item.Price === 0) {
    // Mark this item as having an invalid price
    orderStore.addInvalidPriceField(itemId);
    return; // Stop processing and let user see the red border
  }

  // Remove from invalid fields if price is valid (greater than 0)
  orderStore.removeInvalidPriceField(itemId);

  if (item.Price < 0) {
    item.Price = 0;
    // Add back to invalid fields since we set it to 0
    orderStore.addInvalidPriceField(itemId);
    return;
  } else if (item.Price > item.OriginalPrice) {
    item.Price = item.Price;
    orderStore.saveOrderItemsToLocalStorage();
  }

  // For non-stock / typed-price items (MISC, TIP, BSUR, offline manual entry), set the price
  // directly in state rather than deriving a discount from an unknown original price.
  if (item.Sku === 'MISC-2' || item.Sku === 'TIP-99'
  || item.Sku.toLowerCase().startsWith('bsur')
  || item.RetailSku.toLowerCase().startsWith('bsur')
  || item.IsOfflineManualEntry
  ) {
    const existingItem = orderStore.state.orderItems.find(orderItem =>
      orderItem.uniqueId === item.uniqueId
    );

    if (existingItem) {
      existingItem.Price = parseFloat(item.Price);
      existingItem.OriginalPrice = existingItem.Price;
      console.log('Check prices', existingItem.Price, item.Price);
      orderStore.calculateTaxes();
      orderStore.saveOrderItemsToLocalStorage();
    }
  } else {
    // For other items, handle the discount logic
    const discount = ((1 - item.Price / item.OriginalPrice) * 100).toFixed(2);
    item.Discount = discount > 0 ? discount : 0;
  }
};

// Handle price blur - reset to original price if empty/zero and clear validation
const handlePriceBlur = (item) => {
  const itemId = item.uniqueId || item.ItemId;
  
  // Check if price is empty, null, undefined, NaN, or zero
  if (item.Price === null || item.Price === undefined || item.Price === '' || isNaN(item.Price) || item.Price === 0) {
    // Reset to original price
    item.Price = parseFloat(item.OriginalPrice);
    // Remove from invalid fields
    orderStore.removeInvalidPriceField(itemId);
    // Force format to 2 decimal places
    item.Price = parseFloat(item.Price).toFixed(2);
  } else {
    // Format valid price to 2 decimal places
    item.Price = parseFloat(item.Price).toFixed(2);
    // Remove from invalid fields in case it was there
    orderStore.removeInvalidPriceField(itemId);
  }

  // Continue with existing validation logic
  if (item.Sku !== 'MISC-2' && item.Sku !== 'TIP-99') {
    checkValueChanged('Price', item);
  }
};

// Handle discount input and recalculate the price based on the original price
// const handleDiscountInput = (item) => {
//   if (item.Discount < 0) item.Discount = 0;
//   if (item.Discount > 100) item.Discount = 100;

//   if (item.Sku === 'MISC-2') {
//     const existingItem = orderStore.state.orderItems.find(orderItem =>
//       orderItem.uniqueId === item.uniqueId
//     );

//     existingItem.Price = (item.OriginalPrice * (1 - item.Discount / 100)).toFixed(2);
//     orderStore.updateDiscountPercentage(item, item.Discount);
//     // orderStore.updateDiscountPercentage(item, 0);
//     orderStore.calculateTaxes();
//     orderStore.saveOrderItemsToLocalStorage();
//   } else {
//     item.Price = (item.OriginalPrice * (1 - item.Discount / 100)).toFixed(2);
//     orderStore.updateDiscountPercentage(item, item.Discount);
//   }
// };
const handleDiscountInput = (item) => {
  // Prevent discount editing for tip items
  if (item.Sku === 'TIP-9') {
    item.Discount = 0;
    return;
  }

  // fallback to 0 when input value is cleared
  if (item.Discount === '' || item.Discount === null || item.Discount === undefined || isNaN(Number(item.Discount))) {
    item.Discount = 0;
  }

  if (item.Discount < 0) item.Discount = 0;
  if (item.Discount > 100) item.Discount = 100;

  // Ensure each MISC item is treated independently
  if (item.Sku === 'MISC-2' || item.Sku.startsWith('BSUR')) {
    // Create a new price calculation that doesn't affect other items
    item.Price = (item.OriginalPrice * (1 - item.Discount / 100)).toFixed(2);
    
    orderStore.calculateTaxes();
    orderStore.saveOrderItemsToLocalStorage();
  } else {
    item.Price = (item.OriginalPrice * (1 - item.Discount / 100)).toFixed(2);
    orderStore.updateDiscountPercentage(item, item.Discount);
  }
};

// Watch for overall discount changes to update item prices in real time
watch(() => props.order.overallDiscount, () => {
  props.order.orderItems.forEach(item => {
    item.Price = (item.OriginalPrice * (1 - props.order.overallDiscount / 100)).toFixed(2);
  });
});

watch(
  () => authStore.isCurrentUserManager,
  { immediate: true }
);

// Check for manager permission to approve the discount
const checkManagerPermission = (item) => {
  authStore.refreshAuthState();
  
  console.log('Current manager state:', authStore.isCurrentUserManager);
  
  if (!authStore.isCurrentUserManager) {
    console.log('Not manager - requesting approval');
    backupDiscountPercentage.value = item.Discount;
    authStore.toggleAddManagerApprovalRequest();
    selectedItemForComment.value = item;
  } else {
    console.log('Manager - showing comment popup');
    showCommentPopup.value = true;
    selectedItemForComment.value = item;
  }
};

// Watch for manager login and show comment popup if necessary
watch(() => authStore.isManagerLoggedIn, (newVal) => {
  if (newVal && selectedItemForComment.value) {
    selectedItemForComment.value.Discount = backupDiscountPercentage.value;
    showCommentPopup.value = true;
  }
});

// Initialize isOpen array when items change
watch(() => props.order.orderItems, (newItems) => {
  isOpen.value = newItems.map(() => false);
});

nextTick(() => {
  tippy('#storeQuantity', {
    content: 'QOH Qty',
  });
});

const originalTaxRates = ref({});

function storeOriginalTaxRates(item) {
  if (!originalTaxRates.value[item.ItemId]) {
    originalTaxRates.value[item.ItemId] = {};
  }

  // Store the original tax rates for each tax type
  item.taxRates.forEach(tax => {
    originalTaxRates.value[item.ItemId][tax.type] = tax.rate;
  });

  console.log('Original Tax Rates Stored:', originalTaxRates.value);
}

const checkTaxRateChanged = (item) => {
  let hasTaxRateChanged = false;

  item.taxRates.forEach(tax => {
    const originalRate = originalTaxRates.value[item.ItemId]?.[tax.type] || 0;
    const currentRate = tax.rate;

    console.log(`Original ${tax.type} Rate:`, originalRate);
    console.log(`Current ${tax.type} Rate:`, currentRate);

    if (currentRate !== originalRate) {
      hasTaxRateChanged = true;
    }
  });

  if (hasTaxRateChanged) {
    selectedItemForComment.value = {
      ...item,
      isTaxChange: true,
    };
    console.log("Tax rate change detected. Selected Item for Comment:", selectedItemForComment.value); // Debugging
    checkManagerPermission(item);
  }
};

const safeTaxRates = (item) => {
  return item.taxRates || orderStore.state.taxes.map(tax => ({
    type: tax.type,
    rate: tax.rate,
    amount: 0
  }));
};

const getTaxRate = (item, taxType) => {
  const rates = safeTaxRates(item);
  const tax = rates.find(t => t.type === taxType);
  return tax ? tax.rate : 0;
};

console.log('Order Store Taxes:', orderStore.state.taxes);

const getTheFirstItemSalesPersonId = computed(() => {
  const itemWithSalesPerson = orderStore.state.orderItems.find(item => item.SalesPersonId);
  console.log('itemWithSalesPerson', itemWithSalesPerson?.SalesPersonId);
  return itemWithSalesPerson?.SalesPersonId;
});

// Keyboard shortcuts statt
const lastQtyInputRef = ref(null);

const focusLastQtyInput = () => {
  if (props.order.orderItems.length === 0) return;
  const lastIndex = props.order.orderItems.length - 1;
  isOpen.value[lastIndex] = true;
  
  nextTick(() => {
    if (lastQtyInputRef.value) {
      lastQtyInputRef.value.focus();
      // toast.info('Last item quantity focused');
    }
  });
};

watch(() => props.order.orderItems, (newItems) => {
  isOpen.value = newItems.map(() => false);
}, { immediate: true });

onMounted(() => {
  keyboardStore.register('Alt+Q', focusLastQtyInput);
});

onUnmounted(() => {
  keyboardStore.unregister('Alt+Q');
});

const resetBackupDiscountPercentage = () => {
  backupDiscountPercentage.value = null;
  selectedItemForComment.value = null;
};
// Keyboard shortcuts end

// DaisyUI Collapse component customeization
// Track which item's price is being edited
const editingIndex = ref(null)
const priceInputRefs = ref([])

function toggleEditPrice(index) {
  // If same item clicked again, toggle off; otherwise set new one
  editingIndex.value = editingIndex.value === index ? null : index
  
  // Focus the input after the next DOM update
  if (editingIndex.value === index) {
    nextTick(() => {
      const input = priceInputRefs.value[index]
      if (input) {
        input.focus()
        input.select()
      }
    })
  }
}

// Helper function to set refs
function setPriceInputRef(el, index) {
  priceInputRefs.value[index] = el
}

const handleEnterKeyOnInput = (index) => {
  // Exit edit mode
  editingIndex.value = null
}

// inlcude tax toggle
const isIncludeTaxChecked = ref(false);
 
</script>

<template>
  <AddManagerApprovalRequest :item="selectedItemForComment" @cancelPending="resetBackupDiscountPercentage" />
  <AddSales :item="selectedItemForSalesmen" />
  <CommentPopup v-if="showCommentPopup" :item="selectedItemForComment"
    :isTaxChange="selectedItemForComment?.isTaxChange || false" @commentSubmitted="handleCommentSubmitted"
    @close="showCommentPopup = false" />
    
  <div class="w-[95%] relative mx-auto flex flex-col gap-2" @click="toggleEditPrice(index)">
    <div v-for="(item, index) in order.orderItems" :key="index" 
         class="collapse rounded-2xl bg-[#f4f5f7]" :class="{ 'border-2 border-purple-500': orderStore.state.addManualOrder }">
      <input type="checkbox" class="control-checkbox" :checked="isOpen[index]" @change="toggleAccordion(index)" />
      <div class="collapse-title collapse-handle-div text-md font-medium flex justify-between items-center py-1 px-3">
        <!-- <span
          class="absolute top-0 left-0 border-2 bg-purple-200 rounded-full flex items-center justify-center w-5 h-5 text-center text-black m-1 p-1">
          {{ item.Qty }}
        </span> -->
        
        <div class="flex gap-4 items-center min-w-0 flex-1">
          <img :src="`${item.ItemImage}`" class="w-12 max-w-12 h-12 rounded-xl m-auto" alt="product-img" />
          <!-- <p class="text-base font-medium">{{ item.ItemName }}</p> -->
          <div class="flex flex-col w-full justify-center min-w-0 flex-1">
            <p
              class="item-name text-base font-medium truncate w-fit max-w-[250px] block whitespace-nowrap overflow-hidden text-ellipsis"
              :title="item.ItemName"
            >
              {{ item.ItemName }}
            </p>

            <!-- Offline manual entry: SKU editable in place (ERP matches on it at sync). -->
            <input
              v-if="item.IsOfflineManualEntry"
              v-model="item.Sku"
              @click.stop
              @input="item.RetailSku = item.Sku"
              placeholder="Enter SKU"
              class="border border-purple-300 rounded text-xs px-2 py-0.5 my-0.5 w-36"
            />

            <span class="flex items-center justify-start ml-0 relative z-[2] shrink-0 w-fit">
              <span class="text-gray-500" style="font-size: 18px;">$</span>
              <input  v-if="editingIndex === index || item.IsOfflineManualEntry" :ref="el => setPriceInputRef(el, index)" @keyup.enter="handleEnterKeyOnInput(index)" type="number" placeholder="Price" class="border-2 rounded-lg text-center" v-model.number="item.Price"
                :max="item.OriginalPrice" :min="0"
                @focus="storeOriginalValue('Price', item)" @click.stop @input="handlePriceInput(item)" @blur="handlePriceBlur(item)" :disabled="!item.IsOfflineManualEntry && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-99' && !item.Sku.toLowerCase().startsWith('bsur') && !item.RetailSku.toLowerCase().startsWith('bsur') && ((!userCanEditPrice && !managerCanEditPrice) || (isOverallDiscountApplied && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-9' && !item.Sku.toLowerCase().startsWith('bsur') && !item.RetailSku.toLowerCase().startsWith('bsur')))" :class="{
                  // Disabled state (overall discount applied)
                  'bg-purple-500 opacity-30 text-white cursor-not-allowed border-purple-500': isOverallDiscountApplied && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-9' && !item.Sku.toLowerCase().startsWith('bsur') && !item.RetailSku.toLowerCase().startsWith('bsur'),
                  // Invalid price state (empty or zero)
                  'border-red-500 bg-red-50': orderStore.hasInvalidPrice(item.uniqueId || item.ItemId),
                  // Normal state
                  'bg-white border-gray-300': !orderStore.hasInvalidPrice(item.uniqueId || item.ItemId) && !(isOverallDiscountApplied && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-9' && !item.Sku.toLowerCase().startsWith('bsur') && !item.RetailSku.toLowerCase().startsWith('bsur'))
                }" />
                <label 
                  v-else 
                  class="price-label text-gray-500"
                  @dblclick="toggleEditPrice(index)"
                  for="price"
                >
                  {{ item.isIncludeTaxChecked ? item?.taxInclusivePrice : item.Price }}
                </label>
            </span>
          </div>
        </div>

        <div class="flex gap-2 justify-between h-full shrink-0">
          <div class="flex items-center gap-2 py-1 relative z-10">
            <input 
              :ref="index === order.orderItems.length - 1 ? (el) => lastQtyInputRef = el : null"
              type="number" 
              class="text-center no-arrows border-2 rounded-lg py-1 w-[40px] h-[36px]" 
              v-model.number="item.Qty"
              :min="1" 
              @input="handleInput(item)"
              @keydown="(e) => { if (e.key === '.') e.preventDefault(); }"
              @dblclick="(e) => e.target.select()"
            />
            <span class="relative flex items-center justify-start">
              <i class="pi pi-percentage absolute left-2 text-gray-500"></i>
              <input
                type="number"
                class="border-2 rounded-lg text-center py-1 pl-6 w-[60px]"
                v-model.number="item.Discount"
                :min="0"
                :max="100"
                @focus="storeOriginalValue('Discount', item)"
                @input="handleDiscountInput(item)"
                @blur="checkValueChanged('Discount', item)"
                :disabled="isOverallDiscountApplied || item.Sku === 'TIP-99' || item.isIncludeTaxChecked"
                :class="{
                  'bg-purple-500 opacity-30 text-white cursor-not-allowed': isOverallDiscountApplied || item.Sku === 'TIP-99',
                  'bg-white': !isOverallDiscountApplied && item.Sku !== 'TIP-99'
                }"
                @dblclick="(e) => e.target.select()"
              />
            </span>

            <Button
              class="relative z-[2] p-2 rounded-full pi pi-trash flex items-center justify-center text-gray-600 hover:text-white hover:bg-red-500 hover:rounded-lg"
              @click="orderStore.deleteOrderItem(item)"
            >
            </Button>
          </div>
          <!-- Show discount percentage dynamically based on overall discount -->
          <div class="w-20 flex flex-col items-end justify-center">
            <p v-show="item.Discount" class="text-[10px] text-right text-gray-600">
              {{ `${item.Discount} % discount applied` }}
            </p>
            <p v-show="order.overallDiscount" class="text-[10px] text-right text-gray-600">
              {{ `${order.overallDiscount} % overall discount applied` }}
            </p>
            <div class="">
              <p class="font-medium" v-if="item.isIncludeTaxChecked">${{ (item.taxInclusivePrice * item.Qty).toFixed(2) }}</p>
              <p class="font-medium" v-else>${{ (item.Price * item.Qty).toFixed(2) }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="collapse-content flex bg-white">
        <div class="collapse-left w-6/12 flex flex-col justify-between py-6">

          <!-- Show and make the product name editable only if the product is a misc, tip, and bsur product -->
          <div v-show="item.Sku === 'MISC-2' || item.Sku === 'TIP-99' || item.Sku.startsWith('BSUR')" class="flex items-center justify-start pb-5">
            <i class="pi pi-pencil w-28" style="font-size: 20px;"></i>
            <input type="text" class="border-2 rounded-lg py-1 pl-2 max-w-[150px]" v-model="item.ItemName" @focus="item.originalName = item.ItemName"
              placeholder="Enter product name" @blur="handleNameInput(item)" />
          </div>
          
          <!-- <div class="flex items-center space-x-2 w-full justify-center gap-5 pt-6">
            <Button class="pi pi-minus p-button-rounded p-2 font-extrabold hover:bg-purple-100 hover:rounded-lg"
              @click="orderStore.decrementOrderItem(item)" style="font-size: 20px;" />
            <input 
              :ref="index === order.orderItems.length - 1 ? (el) => lastQtyInputRef = el : null"
              type="number" 
              class="text-center no-arrows border-2 rounded-lg py-1 w-12" 
              v-model.number="item.Qty"
              :min="1" 
              @input="handleInput(item)"
            />
            <Button class="pi pi-plus p-button-rounded font-extrabold p-2 hover:bg-purple-100 hover:rounded-lg"
              @click="orderStore.incrementOrderItem(item)" style="font-size: 20px;" />
          </div> -->
          <div class="flex items-center py-1">
            <p class="w-28 py-1">RetailSku:</p>
            <p class="" style="margin-left: 5px;">{{ item.RetailSku }}</p>
          </div>

          <div class="flex items-center pt-5">
            <label for="storeQuantity" class="w-28">Store QOH</label>
            <span id="storeQuantity" class="flex items-center gap-2">
              <i 
                class="pi pi-shop rounded-lg p-1" 
                style="font-size: 20px; margin-left: 5px;"
                :class="{
                  'bg-red-400': getItemStockStatus(item) === 'red',
                  'bg-yellow-400': getItemStockStatus(item) === 'yellow'
                }"
              ></i>
              <p class="">{{ item.MaxQty }}</p>
            </span>
            <div v-show="userCanAllowFlatTaxRate" class="py-1 flex items-center gap-2 self-end ml-auto">
              <Label>+Tax</Label>
              <input @click="orderStore.checkIncludeTax(item)" 
                   :disabled="isOverallDiscountApplied || (item.Discount && item.Discount > 0)"
                   :checked="item.isIncludeTaxChecked" type="checkbox"  class="toggle" 
                   />
            </div>
          </div>

          <div class="flex gap-2 pt-5">
            
            <button @click="handleAddSales(item)" class="flex items-center">
              <div class="w-28 text-left">
                <i class="pi pi-user bg-purple-200 p-1 rounded-full" style="font-size: 20px;"></i>
              </div>
              <p class="ml-2">{{ item.SalesPersonId ? item.SalesPersonId.fullName : 'Select a salesperson' }}</p>
              <p v-if="orderStore.isManualOrder">{{ getTheFirstItemSalesPersonId }}</p>
            </button>

          </div>
        </div>

        <div class="w-6/12 pt-6 flex flex-col items-start ml-12">
          <!-- Price and Discount Input -->
          <!-- <span class="flex items-center justify-start gap-2">
            <i class="pi pi-dollar" style="font-size: 24px;"></i>
            <input type="number" class="border-2 rounded-lg w-28 text-center py-1" v-model.number="item.Price"
              :max="item.OriginalPrice" :min="0"
              @focus="storeOriginalValue('Price', item)" @input="handlePriceInput(item)" @blur="handlePriceBlur(item)" :disabled="!item.IsOfflineManualEntry && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-99' && ((!userCanEditPrice && !managerCanEditPrice) || (isOverallDiscountApplied && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-9'))" :class="{
                // Disabled state (overall discount applied)
                'bg-purple-500 opacity-30 text-white cursor-not-allowed border-purple-500': isOverallDiscountApplied && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-9',
                // Invalid price state (empty or zero)
                'border-red-500 bg-red-50': orderStore.hasInvalidPrice(item.uniqueId || item.ItemId),
                // Normal state
                'bg-white border-gray-300': !orderStore.hasInvalidPrice(item.uniqueId || item.ItemId) && !(isOverallDiscountApplied && item.Sku !== 'MISC-2' && item.Sku !== 'TIP-9')
              }" />
          </span> -->

          <!-- <span class="flex items-center justify-start gap-2">
            <i class="pi pi-percentage w-16" style="font-size: 24px;"></i>
            <input type="number" class="border-2 rounded-lg w-28 text-center py-1" v-model.number="item.Discount"
              :min="0" :max="100" @focus="storeOriginalValue('Discount', item)" @input="handleDiscountInput(item)"
              @blur="checkValueChanged('Discount', item)" :disabled="isOverallDiscountApplied || item.Sku === 'TIP-99' || item.isIncludeTaxChecked" :class="{
                'bg-purple-500 opacity-30 text-white cursor-not-allowed': isOverallDiscountApplied || item.Sku === 'TIP-99',
                'bg-white': !isOverallDiscountApplied && item.Sku !== 'TIP-99'
              }" />
          </span> -->

          <!-- Dynamically Display Taxes -->
          <div v-for="(tax, taxIndex) in orderStore.state.taxes" :key="tax.type" class="flex items-center justify-start gap-2" :class="{ 'pb-5': taxIndex === 0 }">
            <p class="font-semibold w-16">{{ tax.type }} %</p>
            <input type="number" class="border-2 rounded-lg w-28 text-center py-1" :value="getTaxRate(item, tax.type)"
              disabled
              @input="(e) => {
                const newRate = parseFloat(e.target.value) || 0;
                orderStore.updateItemTaxRate(item, tax.type, newRate);
              }" @focus="storeOriginalTaxRates(item)" @blur="checkTaxRateChanged(item)" :min="0" :max="100" />
            ${{ (item.Price * item.Qty * getTaxRate(item, tax.type) / 100).toFixed(2) }}
          </div>
        </div>
      </div>
    </div>
    <ProductPlaceholder v-show="authStore.isBarcodeSearchFromBackend || authStore.isRetailSkuSearchFromBackend"/>
  </div>
</template>


<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/*daisy ui toggle switch custom bg*/
input.toggle:checked {
  background-color: #a855f7 ;
}
</style>
